import {
	Box,
	Typography,
	makeStyles,
	useMediaQuery,
	useTheme,
} from "@material-ui/core";
import ZoomInIcon from "@material-ui/icons/ZoomIn";
import { CellSize } from "Components/Calendar/CalendarHelper";
import { useCalendar } from "Components/Common/hooks/useCalendar";
import { useIsIsoWeekdayEnabled } from "Components/CompanySetting/CompanySettingHelper";
import { useVisibleMinutes } from "Components/WorkingHours/hooks/useVisibleMinutes";
import { color, px } from "Configs/theme";
import { useTranslation } from "Providers/TranslationProvider";
import moment from "moment";
import React, { FC, Fragment } from "react";
import { CalendarType } from "./NavigationHelper";

interface Props {
	cellSize: CellSize;
}

const weekdays = Array(7)
	.fill(null)
	.map((_, index) => index);

const NavigationCalendarWeekView: FC<Props> = (props) => {
	const theme = useTheme();
	const visibleMinutes = useVisibleMinutes();
	const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
	const { t } = useTranslation();
	const isIsoWeekdayEnabled = useIsIsoWeekdayEnabled();
	const startOfWeek = useCalendar((state) => state.startOfWeek);
	const classes = useStyles({
		dayWidth: props.cellSize.week.dayWidth,
		nameWidth: props.cellSize.week.nameWidth,
		navigationCalendarHeight: props.cellSize.navigation.calendarHeight,
		isSmallScreen,
	});

	function renderDayColumn(weekday: number) {
		const day = startOfWeek.clone().add(weekday, "days");

		if (isIsoWeekdayEnabled(day.isoWeekday())) {
			const visibleTimeStart = moment()
				.startOf("day")
				.add(visibleMinutes.start, "minutes")
				.format(t("Common_Format_Time"));
			const visibleTimeEnd = moment()
				.startOf("day")
				.add(visibleMinutes.end, "minutes")
				.format(t("Common_Format_Time"));

			return (
				<Box className={classes.dayColumn} key={weekday}>
					<Box
						className={classes.dayBox}
						color={
							moment().isSame(day, "day") ? color.primary : color.lightText
						}
						onClick={() => {
							if (!isSmallScreen) {
								useCalendar.getState().setSelectedDate(day);
								useCalendar.getState().setCalendarType(CalendarType.DAY);
							}
						}}
					>
						<Box className={classes.dayContent}>
							<Typography variant="h6" style={{ lineHeight: 1 }}>
								{day.format("D")}
							</Typography>
							<Box>{day.format(isSmallScreen ? "ddd" : "dddd")}</Box>
						</Box>
						{!isSmallScreen && (
							<Box className={classes.dayZoom}>
								<ZoomInIcon fontSize="large" />
							</Box>
						)}
					</Box>
					{!isSmallScreen && (
						<Box
							color={color.lightText}
							display="flex"
							pl={px.s}
							pr={px.s}
							fontSize="10px"
							justifyContent="space-between"
						>
							<div>{visibleTimeStart}</div>
							<div>{visibleTimeEnd}</div>
						</Box>
					)}
				</Box>
			);
		}
	}

	return (
		<Box className={classes.root}>
			<Box className={classes.mainColumn}>
				{isSmallScreen && (
					<Fragment>
						<Box className={classes.mainCalendarWeek}>
							{t("Common_Week", {
								week: startOfWeek.week(),
							})}
						</Box>
						<Box className={classes.mainMonthYear}>
							{startOfWeek.format("MMM YYYY")}
						</Box>
					</Fragment>
				)}
			</Box>
			{weekdays.map(renderDayColumn)}
		</Box>
	);
};

export default NavigationCalendarWeekView;

interface StyleProps {
	nameWidth: number;
	dayWidth: number;
	navigationCalendarHeight: number;
	isSmallScreen: boolean;
}

const useStyles = makeStyles({
	root: {
		display: "flex",
		borderBottom: `1px solid ${color.tableCellBorder}`,
	},
	mainColumn: (props: StyleProps) => ({
		display: "flex",
		flexDirection: "column",
		justifyContent: "center",
		alignItems: "center",
		letterSpacing: "1px",
		fontSize: "16px",
		fontWeight: "bold",
		height: props.navigationCalendarHeight,
		width: props.nameWidth - 1,
	}),
	mainCalendarWeek: {
		fontSize: "16px",
	},
	mainMonthYear: {
		fontSize: "12px",
		textAlign: "center",
		lineHeight: "1.3",
	},
	dayColumn: (props: StyleProps) => ({
		display: "flex",
		flexDirection: "column",
		justifyContent: "center",
		borderLeft: `1px solid ${color.tableCellBorder}`,
		fontSize: "10px",
		fontWeight: 500,
		letterSpacing: "1px",
		textTransform: "uppercase",
		width: props.dayWidth - 1,
	}),
	dayBox: (props: StyleProps) => ({
		display: "flex",
		flexDirection: "column",
		justifyContent: "center",
		textAlign: "center",
		flex: 1,
		cursor: props.isSmallScreen ? "default" : "pointer",
		position: "relative",
		...(!props.isSmallScreen && {
			"&:hover": {
				"& > $dayContent": {
					opacity: 0.2,
					transform: "scale(0.9)",
				},
				"& > $dayZoom > svg": {
					opacity: 1,
					transform: "scale(1)",
				},
			},
		}),
	}),
	dayContent: {
		transition: "all 250ms ease",
	},
	dayZoom: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		position: "absolute",
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		"& > svg": {
			transition: "all 250ms ease",
			transform: "scale(0.5)",
			opacity: 0,
		},
	},
});
